/** Minimal catalog shape for fingerprinting — avoids importing excelAIService. */
export interface TemplateEmbeddingCatalogRow {
  key: string;
  name: string;
  category?: string | null;
  description?: string | null;
  inputTypes?: string[];
}

const DB_NAME = "finster-template-embeddings";
const DB_VERSION = 1;
const STORE_NAME = "catalogs";

export interface PersistedTemplateEmbeddingEntry {
  text: string;
  vector: number[];
}

interface PersistedCatalogRecord {
  fingerprint: string;
  model: string;
  updatedAt: number;
  entries: Record<string, PersistedTemplateEmbeddingEntry>;
}

function hashString(value: string): string {
  let hash = 5381;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

export function computeTemplateCatalogFingerprint(
  templates: TemplateEmbeddingCatalogRow[],
  model: string,
  buildEmbedText: (template: TemplateEmbeddingCatalogRow) => string,
): string {
  const payload = templates
    .slice()
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((t) => `${t.key}\n${buildEmbedText(t)}`)
    .join("\n---\n");
  return hashString(`${model}\n${payload}`);
}

function openDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);

  return new Promise((resolve) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => resolve(null);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "fingerprint" });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
}

export async function loadPersistedTemplateEmbeddings(
  fingerprint: string,
  model: string,
): Promise<Map<string, PersistedTemplateEmbeddingEntry> | null> {
  const db = await openDatabase();
  if (!db) return null;

  try {
    const record = await new Promise<PersistedCatalogRecord | null>((resolve) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(fingerprint);
      request.onsuccess = () => resolve((request.result as PersistedCatalogRecord) ?? null);
      request.onerror = () => resolve(null);
    });

    if (!record || record.model !== model || !record.entries) return null;

    const map = new Map<string, PersistedTemplateEmbeddingEntry>();
    for (const [key, entry] of Object.entries(record.entries)) {
      if (Array.isArray(entry?.vector) && entry.vector.length > 0 && entry.text) {
        map.set(key, entry);
      }
    }
    return map.size > 0 ? map : null;
  } finally {
    db.close();
  }
}

export async function persistTemplateEmbeddings(
  fingerprint: string,
  model: string,
  entries: Record<string, PersistedTemplateEmbeddingEntry>,
): Promise<void> {
  const db = await openDatabase();
  if (!db || Object.keys(entries).length === 0) return;

  try {
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const record: PersistedCatalogRecord = {
        fingerprint,
        model,
        updatedAt: Date.now(),
        entries,
      };
      const request = store.put(record);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });
  } finally {
    db.close();
  }
}
